// Reads keyed by session, shared by every list that shows the same sessions:
// what each session was about (getSessionHeads), what work it was
// (getSessionTopics), whose machine it ran on (getMachinePeople). One store per
// read (its method and other params, under the signed-in context and the
// cluster generation), each session kept in it for a few minutes once read. A
// reader asks only for the sessions the store has not read, so the sidebar's
// page, a card's list and the board's sample, which mostly hold the same
// sessions, read each of them once, and a narrowed search reads only what it
// newly reaches.
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { query } from "./dataClient.js";
import { getAuthContextEpoch, onAuthContextChange } from "./authTokenStore.js";
import { getGenerationRevision, onGenerationPinChange } from "./generationPin.js";
import { useMyPermissions } from "./permissions.js";
import { queryPermitted } from "./queryGate.js";

const KEEP_MS = 5 * 60_000;
// Sessions one store keeps before it starts again: a long-lived page browsing
// many ranges stays bounded.
const MAX_KEPT = 20_000;

let stores = new Map();
export function _resetKeyedReadsForTests() { stores = new Map(); }

function storeFor(key) {
  let store = stores.get(key);
  if (!store) {
    store = { at: new Map(), pending: new Set(), failed: new Map(), values: {}, version: 0, listeners: new Set() };
    stores.set(key, store);
  }
  return store;
}

function changed(store) {
  store.version += 1;
  for (const fn of store.listeners) fn();
}

// Signed in to another context, or onto a new cluster generation, every
// reader draws again under its new key and reads afresh; what another
// context read is let go of.
onAuthContextChange(() => {
  const was = stores;
  stores = new Map();
  for (const store of was.values()) changed(store);
});
onGenerationPinChange(() => {
  for (const store of stores.values()) changed(store);
});

// The sessions of `ids` the store has not read (or read too long ago), read
// `chunk` at a time, each landing as it comes. Answers merge by their own keys
// (a session's, or a machine's); `keep` keeps a key's first answer, so a
// narrower read cannot change what a wider one said.
async function readMissing(store, method, rest, ids, { keepMs, chunk, keep }) {
  const now = Date.now();
  const want = ids.filter((id) => !store.pending.has(id) && !(now - (store.at.get(id) ?? -Infinity) < keepMs));
  if (!want.length) return;
  if (store.at.size + want.length > MAX_KEPT) {
    store.at = new Map();
    store.values = {};
  }
  for (const id of want) store.pending.add(id);
  changed(store);
  for (let i = 0; i < want.length; i += chunk) {
    const part = want.slice(i, i + chunk);
    try {
      const out = (await query(method, { ...rest, ids: part })) || {};
      store.values = keep ? { ...out, ...store.values } : { ...store.values, ...out };
      const at = Date.now();
      for (const id of part) {
        store.at.set(id, at);
        store.failed.delete(id);
      }
    } catch (error) {
      for (const id of part) store.failed.set(id, error);
    }
    for (const id of part) store.pending.delete(id);
    changed(store);
  }
}

// -> { data, ready, loading, error }: `data` every answer the store holds
// (look a session, or a machine, up in it), `ready` once every id asked about
// has been read at least once, `loading` while any of them is being read,
// `error` while one of them could not be (it is asked for again with the
// next ids, or the next reader).
export function useKeyedReads(method, ids, rest = {}, { enabled = true, keepMs = KEEP_MS, chunk = Infinity, keep = false } = {}) {
  const caps = useMyPermissions();
  const on = enabled && queryPermitted(method, caps);
  const restKey = JSON.stringify(rest ?? {});
  const key = `${getAuthContextEpoch()}:${getGenerationRevision()}:${method}:${restKey}`;
  const store = storeFor(key);
  const subscribe = useCallback((fn) => {
    store.listeners.add(fn);
    return () => store.listeners.delete(fn);
  }, [store]);
  const read = useCallback(() => store.version, [store]);
  const version = useSyncExternalStore(subscribe, read, read);
  const idsKey = ids.join(",");
  useEffect(() => {
    if (!on || !ids.length) return;
    readMissing(store, method, JSON.parse(restKey), ids, { keepMs, chunk, keep });
  }, [on, store, method, restKey, idsKey, keepMs, chunk, keep]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(() => {
    const ready = ids.every((id) => store.at.has(id));
    const loading = on && ids.some((id) => store.pending.has(id) || (!store.at.has(id) && !store.failed.has(id)));
    const error = ready ? null : ids.reduce((e, id) => e || (store.pending.has(id) ? null : store.failed.get(id)) || null, null);
    return { data: store.values, ready, loading, error };
  }, [store, version, idsKey, on]); // eslint-disable-line react-hooks/exhaustive-deps
}
